import type { WrittenPage } from '../../sections'

/**
 * The Tools hub in Turkish, written 2026-08-30.
 *
 * Only the hub. Each tool page is its own file here, even though English keeps
 * all four in one: `tools-guts-check.ts` and `tools-ari.ts` were written on
 * 2026-08-31, once those two tools spoke Turkish inside
 * (`content/tools/guts-check.tr.ts`, `ari.tr.ts`) — a Turkish frame around an
 * English twelve-question quiz reads worse than an English page does, which is
 * why they waited. `toolsDiagnostic` still falls back to English.
 *
 * Product and role names stay English on purpose — "Agentic Readiness Index",
 * "Guts Check", "Forward Deployed Engineer": a Turkish executive says these in
 * English and translating them would cost a sentence explaining the translation.
 */
export const tools: WrittenPage = {
  navLabel: 'Araçlar',
  title: 'Değerlendirme araçları',
  description:
    'Dört kısa araç: kurumunuz agentic AI’a ne kadar hazır, hedefiniz icra gücünüzden ne kadar uzakta, düşündüğünüz dönüşüm kendi işletme modelinizle karşılaşınca ayakta kalır mı ve teknoloji bütçeniz gerçekte ne getirebilir.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Araçlar',
      headline: 'Gerçekte nerede olduğunuzu görün.',
      sub: 'Dört araç; hepsi birinin çizdiği bir olgunluk modelinden değil, büyük kurumların içinde gördüklerimizden çıktı. Üçü nerede olduğunuzu puanlıyor; dördüncüsü halihazırda ödediğiniz teknolojinin ne getirebileceğini rakama döküyor. Hiçbiri altı dakikadan uzun sürmüyor ve her biri tartışabileceğiniz bir sonuçla bitiyor.',
      ctas: [],
    },
    {
      kind: 'cards',
      heading: 'Cevabına ihtiyacınız olan soruyu seçin',
      // İngilizcesiyle aynı (01.09.2026): CTA'lar tekrar sola dayalı, mavi
      // renkli "Start →" yazısı oldu (buton değil); ctaLarge bunu bir kademe
      // büyütüyor. Başlığın görünümü değişmiyor — bağlantı rengi ve kalınlığı
      // devralıyor, altı çizilmiyor.
      ctaLarge: true,
      linkTitles: true,
      items: [
        {
          kicker: '12 SORU · 4 DAKİKA',
          title: 'The Guts Check',
          promise: 'Dört seviyeden gerçekte hangisindesiniz ve hedefiniz, altındaki en zayıf halkadan ne kadar uzakta.',
          points: [
            'Hedef, sahiplik, icra ve kanıt; ayrı ayrı puanlanıyor',
            'Hedef–gerçeklik farkı: kapsamın daralacağını önceden söyleyen sayı',
            'Seviyenize özel, önce yapılacak üç iş',
          ],
          cta: { label: 'Başlayın', href: '/en/tools/agentic-guts-check' },
        },
        {
          kicker: '18 SORU · 6 DAKİKA',
          title: 'Agentic Readiness Index',
          promise: 'İşinizin ne kadarını gerçekten bir agent’a bırakırdınız — sorunun iki tarafı da puanlanıyor.',
          points: [
            'Altı boyut: işin neye izin verdiği ve sistemlerin neyi yapabildiği',
            'Bir arketip: hedef odaklı mı, kabiliyet odaklı mı',
            'Sizi ilk durduracak tek boşluk',
          ],
          cta: {
            label: 'Başlayın',
            href: '/en/tools/agentic-readiness-index',
          },
        },
        {
          kicker: '8 SORU · 3 DAKİKA',
          title: 'Transformation Readiness Diagnostic',
          promise: 'Düşündüğünüz dönüşüm, ilk zorlu çeyreğini atlatır mı.',
          points: [
            'Niyet ve kudret; iki ayrı eksen olarak ölçülüyor',
            'Türkçe ve İngilizce',
            'Doğal sonraki adım: otuz dakikalık bir görüşme',
          ],
          cta: {
            label: 'Başlayın',
            href: '/en/tools/transformation-readiness',
          },
        },
        {
          kicker: '4 GİRDİ · 2 DAKİKA',
          title: 'Technology ROI Calculator',
          promise: 'Teknoloji bütçenizin bir yılda ne getirebileceği, fayda hesaplarının çoğunun ikisini atladığı üç parçayla.',
          points: [
            'Sektörünüzün IT harcaması ve Run / Grow / Transform dağılımı',
            'Somut değer, soyut değer ve yerinde saymanın maliyeti',
            'Bir tedarikçinin çarpanına göre değil, gerçek değer vakalarına göre kalibre edildi',
          ],
          cta: {
            label: 'Başlayın',
            href: '/en/tools/roi-calculator',
          },
        },
      ],
    },
    {
      kind: 'cta',
      heading: 'Konuşarak ilerlemeyi mi tercih edersiniz?',
      sub: 'Bunu daha önce yapmış biriyle kırk beş dakika; sunum yok.',
      ctas: [{ label: 'Bize Ulaşın', href: '#contact' }],
      tone: 'band',
    },
  ],
}
